import React, { DetailedHTMLProps, HTMLAttributes } from "react";
import { Product } from "~/src/types/Algolia";
import clsx from "clsx";
import { subDays } from "date-fns";
import * as classesX from "./ProductCard.module.css";
import qs from "qs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCartPlus,
  faExclamationCircle,
  faHeart as faHeartSolisd,
} from "@fortawesome/pro-solid-svg-icons";
import { faHeart, faShippingFast, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { usePLPContext } from "../../ProductListingPage/ProductListingPage";
import chroma from "chroma-js";
import { useMedia } from "react-use";
import { ProductPrice } from "../ProductPrice";
import ProductLightBox from "./ProductLightBox";
import { VariantSelector } from "./VariantSelector";
import { WishlistSelector } from "./WishlistSelector";
import { ProductTimer } from "./ProductTimer";
import { group } from "console";
import { preventOverflow } from "@popperjs/core";
import ProductGallerySlider from './ProductGallerySlider';

const classes = classesX;

type HitProps = {
  hit: Product;
  queryID?: string;
};

type DialogProps = {
  open?: boolean;
  onClose?: (reason: string) => void;
  lockScroll?: boolean;
};

type DialogComponentProps = DialogProps &
  Omit<
    DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    keyof DialogProps
  >;

const Dialog = ({
  open = false,
  onClose,
  children,
  onKeyDown,
  lockScroll = true,
  ...props
}: DialogComponentProps) => {
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (open) {
      window.trapFocus(rootRef.current);

      return () => {
        window.removeTrapFocus();
      };
    }
  }, [open]);

  React.useEffect(() => {
    if (open && lockScroll) {
      document.body.classList.add("overflow-hidden");

      return () => {
        document.body.classList.remove("overflow-hidden");
      };
    }
  }, [open, lockScroll]);

  React.useEffect(() => {
    if (open) {
      const onClickAway = (event) => {
        if (!rootRef.current.contains(event.target)) {
          event.preventDefault();
          onClose("clickAway");
        }
      };

      document.addEventListener("click", onClickAway);

      return () => {
        document.removeEventListener("click", onClickAway);
      };
    }
  }, [open]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        onClose("escapeKey");
        break;
      default:
        break;
    }
    onKeyDown && onKeyDown(event);
  };

  return (
    <div
      {...props}
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
};

export const ProductCard = ({ hit, queryID }: HitProps) => {
  const { b2b, handle, collection, customerTags, visual_percentage_off_tab } = usePLPContext();

  const isMedium = useMedia("(min-width: 768px)");

  const rootRef = React.useRef<HTMLDivElement>(null);

  const [cartState, setCartState] = React.useState({
    loading: null,
    open: false,
  });

  const [wishState, setWishState] = React.useState({
    loading: null,
    open: false,
    ready: false,
    wishedVariants: {},
  });

  const title = React.useMemo(() => hit.title.split(" - ")[0], [hit.title]);
  const subtitle = React.useMemo(
    () => hit.title.split(" - ").slice(1).join(" - "),
    [hit.title]
  );

  //Only used if product is a Group Product
  const selectionText = React.useMemo(() => hit.title.split(" - ")[1], [hit.title])

  const [currency, setCurrency] = React.useState<string>(() => {
    return (
      window.localStorage.getItem("activeCurrency") || window.default_currency
    );
  });

  React.useEffect(() => {
    const onCurrencyChange = (event: CustomEvent<{ currency: string }>) => {
      setCurrency(event.detail.currency);
    };

    window.addEventListener("currency_changed", onCurrencyChange);

    return () => {
      window.removeEventListener("currency_changed", onCurrencyChange);
    };
  }, []);

  /**
   * Handle scroll restoration on product listing pages. Only applicable for
   * browsers other than Safari that do not restore the page from a cache.
   */
  React.useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        history.scrollRestoration = "auto";
      } else {
        history.scrollRestoration = "manual";
        const parsed = qs.parse(window.location.search, {
          ignoreQueryPrefix: true,
        });

        if (parsed.id === hit.objectID) {
          if(rootRef.current) 
            rootRef.current.scrollIntoView();
        }
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("popstate", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("popstate", handlePageShow);
    };
  }, []);

  React.useEffect(() => {
    let mounted = true;

    const onInit = (products) => {
      const wishedVariants = products
        .filter(({ empi }) => empi === hit.id && !isGroupProduct || isGroupProduct && variants.filter(x => x.prod === empi))
        .reduce(
          (acc, { epi }) => ({
            ...acc,
            [epi]: true,
          }),
          {}
        );
      if (mounted) {
        setWishState((prev) => ({
          ...prev,
          ready: true,
          wishedVariants,
        }));
      }
    };

    if ("_swat" in window) {
      window._swat.fetch(onInit);
    } else {
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push((swat) => {
        swat.fetch(onInit);
      });
    }

    return () => {
      mounted = false;
    };
  }, []);

  // Check for athlete tag
  let atheleTagImage = false
  let athleteProperty = {}
  if(collection?.athleteTag){
    hit.images.forEach(img => {
      if(img.src.split('ATHLETE_')[1]?.split('.')[0] == collection.athleteTag){
        atheleTagImage = img
        athleteProperty = { '_Athlete' : `ATHLETE_${collection.athleteTag}` }
      }
    })
  }

  const url =
    (handle ? `/collections/${handle}` : "") + `/products/${hit.handle}` + `${collection?.athleteTag ? '?athlete='+collection?.athleteTag : ''}`;


  const handleClickThrough = (event: React.MouseEvent<HTMLAnchorElement>) => {

    const previousQuery = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });
    const nextQuery = qs.stringify(
      {
        ...previousQuery,
        id: hit.objectID,
      },
      {
        encodeValuesOnly: true,
      }
    );
    window.history.replaceState(
      {},
      null,
      `${window.location.pathname}?${nextQuery}`
    );
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "hitClick",
      eventModel: {
        objectID: [hit.objectID],
        position: [hit.__position],
        queryID,
      },
    });
};

  const isBra = hit.product_type.toLowerCase() === "sports bras" || hit.product_type.toLowerCase() === "bralettes"; //Check if Bra
  const isTee = hit.product_type.toLowerCase() === "t-shirts" ; //Check if Tee
  const seamlessFabric = hit.tags.some((tag) => tag.toLowerCase().includes('seamless-fabric'));
  
  const fabricWeight = isTee && hit.tags.some((tag) => tag.toLowerCase().includes('facet:weight')) ? 
    window.textContent.facets.weight[hit.tags.filter((tag) => tag.toLowerCase().includes('facet:weight'))[0].toLowerCase()]
    : false;

  const productCut = hit.tags.some((tag) => tag.toLowerCase().includes('facet:cut')) ? 
    window.textContent.facets.cut[hit.tags.filter((tag) => tag.toLowerCase().includes('facet:cut'))[0].toLowerCase()]
    : false;

  const supportType = isBra && hit.tags.some((tag) => tag.toLowerCase().includes('facet:support')) ? 
    window.textContent.facets.support[hit.tags.filter((tag) => tag.toLowerCase().includes('facet:support'))[0].toLowerCase()]
    : false;
  
  const veganFriendly = hit.tags.some((tag) => tag.toLowerCase().includes('badge:vegan friendly'));
  const newFormula = hit.tags.some((tag) => tag.toLowerCase().includes('badge:new formula'));
  const isSupplement = hit.tags.some((tag) => tag.toLowerCase().includes('all products: supplements')); //Check if Supplement Product
  const isGroupProduct = hit.tags.some((tag) => tag.toLowerCase().includes('group:')); //Check if Grouped Product
  const setTag = String(hit.tags.filter(tag => {
    if(tag.toLowerCase().includes('group:'))
      return tag
    else
      return ''
  }))
  const limitOne = hit.tags.some((tag) => tag.toLowerCase().includes('limit-1'));

  const variants = (isGroupProduct ?
      // JSON.parse(window.groupedProducts).groupedProducts
      JSON.parse(window.groupedProducts).filter(p => p.tags.includes(setTag.split('group:')[1]))
    :
      hit.variants
  ); //Checks to see if product is a Group Product. If it is, it will look for custom JSON variant feed and supply it instead of hit.variants

  const unavailableVariants = variants.filter(v => v.inventory_policy === "deny" && v.inventory_quantity <= 0 );

  const isAvailable = React.useMemo(
    () =>
      variants.some(
        ({ inventory_quantity, inventory_policy }) =>
          inventory_policy === "continue" || inventory_quantity > 0
      ),
    [hit]
  );

  // Total count of products over all variants.
  const inventoryTotalCount = variants.reduce((accumulator, variant) => accumulator + variant.inventory_quantity, 0);

  // Checks if the product is checking for the 'preorder:#' tag and check
  // if the inventory total is lower than the preorder check.
  const preorderCheck = () => {
    let approved = false;
    hit.tags.forEach(tag => {
      if(tag.toLowerCase().indexOf('preorder:') !== -1){
        if(Number(tag.toLowerCase().split('preorder:')[1]) > inventoryTotalCount){
          approved = true;
        }
      }
    })
    return approved;
  }

  const capitalise = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  // const shop_at = hit.tags.some((tag) => tag.toLowerCase().includes('shop_at:')) ? () => {
  //   let shop = hit.tags.filter((tag) => tag.toLocaleLowerCase().includes('shop_at:'))[0].split(':')[1].trim()
  //   return shop.charAt(0).toUpperCase() + shop.slice(1)
  // } : false;

  const ignoreSoonTag = customerTags ? customerTags.some((tag) => tag.toLowerCase().includes('ignore-soon')) : false; // Ignore Soon tag | Meant for Admin product testing.
  const hasSoonTag = ignoreSoonTag ? false : hit.tags.some((tag) => tag.toLowerCase() === "soon");
  const shop_at = hit.tags.some((tag) => tag.toLowerCase().includes('shop_at:')) ? capitalise(hit.tags.filter((tag) => tag.toLocaleLowerCase().includes('shop_at:'))[0].split(':')[1].trim()): false;
  const loginRequired = hit.tags.some((tag) => tag.toLowerCase() === "login-required") && document.querySelector('body.logged-in');
  const hasPreorderTag = hit.tags.some((tag) => tag.toLowerCase() === "preorder") || preorderCheck();
  const isSoldAtColes = hit.tags.some((tag) => tag.toLowerCase() === "coles");
  const hasRolloverTag = hit.tags.some((tag) => tag.toLowerCase() === "rollover-bundle");
  const isChemistWarehouse = hit.tags.some((tag) => tag.toLowerCase() === "chemist-warehouse");
  // const onlyAtColes = hit.tags.some((tag) => tag.toLowerCase() === "coles");
  const appExclusive = hit.tags.some((tag) => tag.toLowerCase() === "app-exclusive") && !hasSoonTag;

  // const hasVariant = hit.options.some(({ name }) => name.toLowerCase() === "size");
  const hasSizeVariant = hit.options.some(({ name }) => name.toLowerCase() === "size");
  const hasVariant = hit.options;
  const hasOneVariant = variants.length === 1;
  const isRTD12Pack = hit.tags.includes("rtd-12-pack")
  // const productLimit = utilities.energyDrinkLimit && isRTD12Pack;
  const notForSale = ["not-for-sale"].some((tag) => hit.tags.includes(tag));
  const isRestocking = hit.tags.includes("restocking");
  const notRestocking = hit.tags.includes("not-restocking");
  const enableSignup = hit.tags.includes("signups-on");
  const isLaunching = ignoreSoonTag ? false : ["prelaunch", "soon"].some((tag) =>
    hit.tags.includes(tag)
  );
  const womensPhotoPreferred = collection?.womensCollection && hit.tags.includes("womens-photo-preferred") && hit.images.filter(img => img.src.includes("-WOMENS-1"))[0] != undefined;
  const grocerSupplement = hit.tags.includes("chemist-warehouse") || hit.tags.includes("coles") || shop_at;
  const lowCarbSupplement = hit.tags.includes("Low Carb");
  

  // Figure out if product is on Sale
  let enableVisualDiscount = false
  if(visual_percentage_off_tab){
    enableVisualDiscount = true
  }else if(window?.sale != null && window?.sale > 0 && window?.sale_tag != null && !window?.B2B){ // && product.product_type != "Gift Cards"){
    enableVisualDiscount = hit.tags.some((tag) => tag.toLowerCase().includes(window?.sale_tag)); //Check if product has window sale tag
  }else if(window?.sale != null && window?.sale > 0 && !window?.B2B){ // && product.product_type != "Gift Cards"){
    enableVisualDiscount = true
  }
  const visualPercentageApplies = b2b ? true : hit.compare_at_price == 0 && enableVisualDiscount
  const productOnSale = hit.compare_at_price != null && hit.price < hit.compare_at_price || visualPercentageApplies;
  
  const comingSoonSignup =
    isLaunching && enableSignup || enableSignup;

  const isBundle = hit.product_type === "Bundle";
  const canAddToCart =
    (hasVariant || hasOneVariant) &&
    !notForSale &&
    !isLaunching &&
    !isBundle &&
    !hasSoonTag &&
    // !productLimit &&
    (isAvailable || !isAvailable && hasPreorderTag);

  // const canWishlist = !notForSale && !isBundle && !isSoldAtColes;
  const canWishlist = !isBundle;

  const isSoldOut =
    !isAvailable && !isLaunching && !notForSale;

  const handleCartClick = async (variantId: number, bundle = false) => {
    setCartState((prev) => ({
      ...prev,
      loading: variantId,
    }));

    const cartNotification = document.querySelector("cart-notification") as any;

    try {
      const json = await fetch(window.routes.cart_add_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: `application/javascript`,
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: cartNotification
            .getSectionsToRender()
            .map((section) => section.section),
          sections_url: window.location.pathname,
          properties: bundle ? bundle : athleteProperty
        }),
      }).then((response) => response.json());

      cartNotification.renderContents(json);

      // Check for AutoGift
      if (typeof window.runAutoGift === 'function') {
        await window.runAutoGift();
      }

      // Check for AutoDonation
      if (typeof window.runAutoDonation === 'function') {
        window.runAutoDonation();
      }

    } catch (error) {
      console.error(error);
    }

    setCartState((prev) => ({
      ...prev,
      open: false,
      loading: null,
    }));
  };

  const breastCancerTag = hit.tags.find((tag) => tag == ("Breast Cancer"));

  let badgeText;

  const badgeTag = hit.tags.find((tag) => tag.startsWith("badge:") && tag.toLowerCase() != ("badge:vegan friendly") && tag.toLowerCase() != ("badge:new formula"));
  const noReturns = hit.tags.some((tag) => tag.toLowerCase() == "no-returns");

  if(noReturns){
    badgeText = "Final Sale"; 
  } else if (badgeTag && !isLaunching) { 
    [badgeText] = badgeTag.slice(6).split(":");
  } else if (hasPreorderTag) {
    badgeText = "Pre-Order";
  } else if (isRestocking && !shop_at) {
    badgeText = "Restocking";
  } else if (isLaunching) {
    badgeText = "Coming Soon";
  } else if (
    hit.available &&
    variants.reduce(
      (acc, { inventory_quantity, inventory_policy }) =>
        inventory_policy === "deny" && inventory_quantity <= 10 ? acc + 1 : acc,
      0
    ) >= Math.ceil(variants.length / 2)
  ) {
    // badgeText = "Going fast";
    badgeText = "";
  } else if (new Date(hit.created_at) >= subDays(new Date(), 30) && !isGroupProduct && !productOnSale) {
    badgeText = "New"; 
  }

  // Overwrite if sold out supplement.
  if (isSupplement && isSoldOut && !notRestocking && !grocerSupplement) {
    badgeText = "Restocking";
  }

  // const hasVisual25 = hit.tags.some((tag) => tag.toLowerCase().includes('visual-25-off'));
  // if(hasVisual25 && hit.compare_at_price == 0){
  //   badgeText = "CODE: SUPPS25 - 25% OFF";
  // }

  let messageText, messageColor, messageContrastColor;

  const messageTag = hit.tags.find((tag) => tag.startsWith("message:"));

  if (messageTag) {
    [messageText, messageColor] = messageTag.slice(8).split(":");
    if (!messageColor) {
      messageColor = "#52525B";
      messageContrastColor = "#FFF";
    } else {
      try {
        messageContrastColor =
          chroma.contrast(messageColor, "#FFF") >= 4.5 ? "#FFF" : "#18181B";
      } catch (error) {
        messageContrastColor = "#FFF";
      }
    }
  }

  const src = atheleTagImage
    ? atheleTagImage : (hit.image = hit.images.filter(img => !img.src.includes("ATHLETE_"))[0]) ? window.Shopify.addSizeToImage(hit.image.src, 1280) : ""

  const secondSrc = hit.images.length > 1 ? window.Shopify.addSizeToImage(hit.images[1].src, 1280) : '';
  
  const preferedWomensImage = womensPhotoPreferred ? hit.images.filter(img => img.src.includes("-WOMENS-1"))[0] : '';
  const preferedWomensImageSRC = womensPhotoPreferred && preferedWomensImage != undefined ? window.Shopify.addSizeToImage(hit.images.filter(img => img.src.includes("-WOMENS-1"))[0].src, 1280) : '';

  const srcSet = React.useMemo(
    () =>
      atheleTagImage?.src
        ? atheleTagImage?.src : hit.image?.src
          ? [320, 480, 800, 1280]
              .map(
                (width, _, arr) =>
                  `${window.Shopify.addSizeToImage((atheleTagImage?.src ? atheleTagImage.src : hit.image.src), width)} ${width / arr[0]}x`
              )
              .join(",")
          : "",
    [atheleTagImage?.src
      ? atheleTagImage.src : hit.image?.src
        ? hit.image.src : null]
  );

  const secondSrcSet = React.useMemo(
    () =>
      hit.images[1]?.src
        ? [320, 480, 800, 1280]
            .map(
              (width, _, arr) =>
                `${window.Shopify.addSizeToImage(hit.images[1].src, width)} ${
                  width / arr[0]
                }x`
            )
            .join(",")
        : "",
    [hit.images[1]?.src ? hit.images[1].src : null]
  );

  const womensImageSRCSet = React.useMemo(
    () =>
      preferedWomensImage?.src
        ? [320, 480, 800, 1280]
            .map(
              (width, _, arr) =>
                `${window.Shopify.addSizeToImage(preferedWomensImage.src, width)} ${
                  width / arr[0]
                }x`
            )
            .join(",")
        : "",
    [preferedWomensImage?.src ? preferedWomensImage.src : null]
  );

  const [currentSrc, setCurrentSrc] = React.useState(src);
  const [currentSrcSet, setCurrentSrcSet] = React.useState(src);
  const [showExtraImage, setShowExtraImage] = React.useState(false);

  const [bundleItem, setBundleState] = React.useState([]);

  const toggleImage = () => {
    if( isGroupProduct )
      setShowExtraImage( !showExtraImage )
  }

  const toggleImageOn = (e) => {
    e.preventDefault();
    toggleImage()
    if(isMedium && !hasOneVariant && document.querySelector('html').classList.contains('cardABTest') && !isSoldOut){
      setCartState((prev) => ({
        ...prev,
        open: true,
      }))
    }
  }
  const toggleImageOff = (e) => {
    e.preventDefault();
    toggleImage()
    if(isMedium && !hasOneVariant && document.querySelector('html').classList.contains('cardABTest')){
      setCartState((prev) => ({
        ...prev,
        open: false,
      }))
    }
  }

  const handleBundleClick = async (variantId: number) => {
    setBundleState(bundleItem => [...bundleItem, variantId])
  }

  const handleWishClick = (variantId: number) => async () => {
    setWishState((prev) => ({
      ...prev,
      loading: variantId,
    }));


    const variant = variants.find(({ id }) => id === variantId);
    const data = {
      epi: variantId,
      empi: isGroupProduct ? variant.prod_id : hit.id,
      du: `${window.location.origin}/products/${isGroupProduct ? variant.handle : hit.handle}`,
      dt: isGroupProduct ? variant.title : hit.title,
      iu: isGroupProduct ? variant.image.src : atheleTagImage ? atheleTagImage.src : hit.image.src,
      pr: Number(variant.price),
      vi: variant.title,
    };

    try {
      await new Promise<void>((resolve) =>
        window._swat.addToWishList(data, resolve)
      );

      setWishState((prev) => ({
        ...prev,
        wishedVariants: {
          ...prev.wishedVariants,
          [variantId]: true,
        },
      }));
    } catch (error) {
      console.error(error);
    }

    setWishState((prev) => ({
      ...prev,
      loading: null,
      open: false,
    }));
  };

  // const aspectRatio = hit.image ? hit.image.width / hit.image.height : 0.8;

  const supportsLoading = "loading" in HTMLImageElement.prototype;

  const messageStyles = {
    "--color": messageColor,
    "--contrastColor": messageContrastColor,
  } as React.CSSProperties;

  const sanitiseTitle = (title) => {
    // Update size option if size variants.
    let result = title
    switch (title) {
      case "XXS":
        result = "2XS";
        break;
      case "XXL":
        result = "2XL";
        break;
      case "XXXL":
        result = "3XL";
        break;
      case "XXXXL":
        result = "4XL";
        break;
      default:
        result = title;
    }
    return result
  }

  function filterGalleryImages(){
    let hitImages = hit.images.filter(img => !img.src.includes("ATHLETE_"))
    
    if(womensPhotoPreferred){
    hitImages.forEach((image, i) => {
        if(image.src.includes("-WOMENS")) {
          hitImages.splice(i, 1)
          hitImages.unshift(image)
        }
      })
    }

    return hitImages
  }

  let enableVisualDiscountBadge = false
  if(window?.sale != null && window?.sale > 0 && window?.sale_tag != null && window?.sale_text != null && !window?.B2B && hit.compare_at_price == 0 && hit.product_type != "Gift Cards"){
    enableVisualDiscountBadge = hit.tags.some((tag) => tag.toLowerCase().includes(window?.sale_tag)); //Check if Product has window sale tag
  }else if(window?.sale != null && window?.sale > 0 && window?.sale_text != null && !window?.B2B && hit.compare_at_price == 0 && hit.product_type != "Gift Cards"){
    enableVisualDiscountBadge = true
  }

  return (
    <div 
      ref={rootRef}
      className={classes.root + ' productCardRoot' + (isGroupProduct ? ' group-product' : '')}
      data-product={hit.id}
      // data-supplement={isSupplement && hit.compare_at_price == 0}
      onMouseLeave={toggleImageOff}
    >
      <div className={classes.mediaWrapper + ' card-mediaWrapper' + ((isMedium || isSupplement) ? '' : ' media-slider')}>
        {(isMedium || isSupplement || atheleTagImage) ? (
          <>
            <div
            className={classes.media + " media media--portrait"}
            // style={{
            //   paddingBottom: `${(1 / aspectRatio) * 100}%`,
            // }}
          >
            {(!showExtraImage || !secondSrcSet || !womensPhotoPreferred) && (
              <img
                alt={hit.title}
                src={supportsLoading ? src : null}
                srcSet={supportsLoading ? srcSet : null}
                data-src={src}
                data-srcset={srcSet}
                className="lazyload"
                loading="eager"
              />
            )}
            {(showExtraImage && secondSrcSet && isMedium && !womensPhotoPreferred) && (
              <img
                alt={hit.title}
                src={supportsLoading ? secondSrc : null}
                srcSet={supportsLoading ? secondSrcSet : null}
                data-src={secondSrc}
                data-srcset={secondSrcSet}
                className="lazyload"
                loading="eager"
              />
            )}
            {(womensPhotoPreferred && womensImageSRCSet) && (
              <img
                alt={hit.title}
                src={supportsLoading ? preferedWomensImageSRC : null}
                srcSet={supportsLoading ? womensImageSRCSet : null}
                data-src={preferedWomensImageSRC}
                data-srcset={womensImageSRCSet}
                className="lazyload"
                loading="eager"
              />
            )}
          </div>
          <a
            href={url}
            onClick={handleClickThrough}
            onMouseEnter={toggleImage}
            onMouseLeave={toggleImage}
            onMouseUp={() => {window.location.href = url}}
            className="link-unstyled"
          >
            <span className="visually-hidden">{hit.title}</span>
          </a>
        </>
        ) : ( 
          <a href={url} className="link-unstyled" onClick={handleClickThrough}><ProductGallerySlider gallery={filterGalleryImages()} productLink={url} hit={hit} /></a>
        )}
 
        {/* <div className={clsx(classes.product_badge_wrapper + ' ' + (isSoldOut ? classes.productSoldOut : ' ') + ' ' + (isSupplement ? classes.isSupplement : '' ))}> */}
        <div className={clsx(`${classes.product_badge_wrapper} ${isSoldOut ? classes.productSoldOut : ''} ${isSupplement ? classes.isSupplement : '' }`)}>
          <div className={classes.product_badges}>

            {(!isSoldOut) && enableVisualDiscountBadge && !isLaunching && window?.sale_text && (
              <span 
                className={classes.badge + ` badge sale_badge_text`}
                style={{
                  backgroundColor: 'var(--sale-badge-background) !important',
                  color: 'var(--sale-badge-color) !important'
                }}
              >
                {window?.sale_text}
              </span>
            )}

            {(!isSoldOut || isSupplement) && badgeText && <span className={classes.badge + ` badge __${badgeText.replace(' ','_').replace(':','_').replace('%','_')}` + ( hasPreorderTag && canAddToCart ? classes.badgePreorder : '' ) + ' '}>{badgeText}</span>}

            {/* {(isRTD12Pack || limitOne) && (
              <p className={classes.subtitle}>
              <i>Limit 1 Per Order Total</i>
              </p>
            )}
            {isRTD12Pack && title.indexOf('MNxEBT ENERGY DRINK') == -1 && (
              <p className={classes.subtitle}>
              <i>Limit 2 Per Order Total</i>
              </p>
            )} */}
            
            {(!isSoldOut && (isRTD12Pack || limitOne)) && (
              <>
                {(!isSoldOut || isSupplement) && badgeText && <span className={classes.badge + ' badge'}>
                  {isRTD12Pack && title.indexOf('MNxEBT ENERGY DRINK') == -1 && (
                    <>
                      Limit 2 Per Order Total
                    </>
                  )}
                  {(isRTD12Pack || limitOne) && (
                    <>
                      Limit 1 Per Order Total
                    </>
                  )}
                </span>}
              </>
            )}

            {womensPhotoPreferred && <span className={classes.badge + ' badge'}>Men's Sizing</span>}

            {/* {!isSoldOut && hasRolloverTag && (
              <span className={classes.badge +" badge rollover"}>
                Buy 2 Save 20% <span>–</span> Buy 3 Save 30%
              </span>
            )} */}
            {breastCancerTag && (
              <svg className={classes.breastCancerBadge} width="96" height="121" viewBox="0 0 96 121" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M62.82 77.5701L74.92 65.4801C82.41 57.9901 86.16 48.1701 86.16 38.3601C86.16 28.5501 82.4 18.7201 74.91 11.2301C59.93 -3.74988 35.64 -3.74988 20.65 11.2301C5.67 26.2101 5.67 50.4901 20.65 65.4701L32.74 77.5601L0 110.32L7.52 117.83C11.65 121.96 18.42 121.96 22.55 117.83L47.78 92.6001L73.02 117.83C77.15 121.96 83.92 121.96 88.05 117.83L95.57 110.32L62.82 77.5701ZM47.78 62.5401L35.69 50.4501C29.01 43.7701 29.01 32.9401 35.69 26.2601C42.37 19.5801 53.2 19.5801 59.88 26.2601C63.22 29.6001 64.89 33.9801 64.89 38.3501C64.89 42.7201 63.22 47.1001 59.88 50.4401L47.78 62.5401Z" fill="#A30D47"/>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M45.0598 95.3199L22.5398 117.83C18.4098 121.96 11.6398 121.96 7.50977 117.83L37.5398 87.8099L45.0598 95.3199ZM88.0498 117.83C83.9198 121.96 77.1498 121.96 73.0198 117.83L20.6598 65.4799C5.67977 50.4999 5.67977 26.2099 20.6598 11.2399C35.6398 -3.74012 59.9298 -3.74012 74.9198 11.2399C82.4098 18.7299 86.1598 28.5499 86.1598 38.3599C86.1598 48.1699 82.3998 57.9899 74.9098 65.4799L62.8098 77.5699L55.2898 70.0599L67.3898 57.9699C78.2198 47.1399 78.2198 29.5799 67.3898 18.7599C56.5598 7.92988 38.9998 7.92988 28.1698 18.7599C22.7498 24.1699 20.0498 31.2699 20.0498 38.3699C20.0498 45.4699 22.7598 52.5599 28.1698 57.9799L88.0498 117.83Z" fill="#DB2463"/>
              </svg>
            )}

            {isSupplement && (veganFriendly || newFormula) && !isSoldOut && (
              <span className={classes.badge + ' badge ' + ' ' + classes.badge_bottom + ' ' + classes.isSupplement }>
                {veganFriendly ? (
                  <>Vegan Friendly</>
                ) : newFormula && (
                  <>New Formula</>
                )}
              </span>
            )}

            {(lowCarbSupplement && !isSoldOut && <span className={classes.badge + ' badge ' + ' ' + classes.badge_bottom + ' ' + classes.isSupplement }>Low Carb</span>)}

          </div>
          {isSoldOut && !shop_at && !collection?.forceWishlist && (
              <div className="sold-out">SOLD OUT</div>
          )}
        </div>
        
        {/* <a
          href={url}
          onClick={handleClickThrough}
          // onMouseEnter={toggleImage}
          // onMouseLeave={toggleImage}
          // onMouseEnter={toggleImageOn}
          onMouseOver={toggleImageOn}
          onMouseUp={() => {window.location.href = url}}

          className="link-unstyled"
        >
          <span className="visually-hidden">{hit.title}</span>
        </a> */}

        <ProductTimer hit={hit}/>


        {/* {!appExclusive && !loginRequired && ( */}
        {!shop_at && (
          <WishlistSelector
            hit={ hit }
            isMedium={ isMedium }
            hasOneVariant={ hasOneVariant }
            canWishlist={ canWishlist }
            isGroupProduct={ isGroupProduct }
            variants={ variants }
            Dialog={ Dialog }
            badgeText={ badgeText }
            title={ title }
            subtitle={ subtitle }
            currency={ currency }
            messageStyles={ messageStyles }
            messageText={ messageText }
            wishState={ wishState }
            setWishState={ setWishState }
            handleWishClick={ handleWishClick }
            sanitiseTitle={ sanitiseTitle }
            cartState={cartState}
            setCartState={setCartState}
          />
        )}
        {/* )} */}


      </div>
      <div className={classes.info + ' card-info'}>
        <div className={'card-info-wrapper'}>
          <a
            tabIndex={-1}
            href={url}
            onClick={handleClickThrough}
            className={classes.header}
          >
            <p className={classes.title}>{title}</p>
            <p className={classes.subtitle}>{subtitle}</p>
            {isBra && supportType && (
              <p className={classes.subtitle}>
                <i>{supportType}</i>
              </p>
            )}
            {seamlessFabric && (
              <p className={classes.subtitle}>
                <i>Seamless Fabric</i>
              </p>
            )}
            {isTee && fabricWeight && (
              <p className={classes.subtitle}>
                <i>{fabricWeight}</i>
              </p>
            )}
            {productCut && (
              <p className={classes.subtitle}>
                <i>{productCut}</i>
              </p>
            )}
          </a>

              {!shop_at && (
                <ProductPrice forceWishlist={collection?.forceWishlist} product={hit} currency={currency} />
              )}
              <p
                className={clsx(classes.message, "hit__message")}
                style={messageStyles}
              >
                {messageText}
              </p>
          <p className="tester"></p>

        </div>

        {(!appExclusive && !loginRequired || hasSoonTag) && (
          <VariantSelector
            cartState={ cartState }
            setCartState={ setCartState }
            hit={ hit }
            isMedium={ isMedium }
            hasSoonTag={ hasSoonTag }
            canWishlist={ canWishlist }
            hasOneVariant={ hasOneVariant }
            canAddToCart={ canAddToCart }
            isGroupProduct={ isGroupProduct }
            hasPreorderTag={ hasPreorderTag }
            selectionText={ selectionText }
            variants={ variants }
            Dialog={ Dialog }
            ProductPrice={ ProductPrice }
            badgeText={ badgeText }
            title={ title }
            subtitle={ subtitle }
            currency={ currency }
            messageText={ messageText }
            hasSizeVariant={ hasSizeVariant }
            messageStyles={ messageStyles }
            unavailableVariants={ unavailableVariants }
            wishState={ wishState }
            handleCartClick={ handleCartClick }
            handleWishClick={ handleWishClick }
            sanitiseTitle={ sanitiseTitle }
            comingSoonSignup={ comingSoonSignup }
            womensPhotoPreferred={ womensPhotoPreferred }
          />
        )}

        {/*
        {productLimit && (
          <button
          disabled
          data-product={hit.id}
          data-title={hit.title}
          className={classes.notify}
          type="button"
        >
          Limit 2 Per Person
        </button>
        )} */}

        {shop_at && (
          <a href={url} data-shop={shop_at}>Shop at {shop_at}</a>
        )}

        {!appExclusive && (isSoldOut || comingSoonSignup) && !collection?.forceWishlist && !shop_at && (
          <button
            data-product={hit.id}
            data-title={hit.title}
            data-variants={btoa(JSON.stringify(variants))}
            className={classes.notify + " notify link bis-link"}
            aria-haspopup="dialog"
            type="button"
          >
            Notify when available
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-chevron-right"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </button>
        )}

        {loginRequired && (
          <a href="/account/login" type="button" aria-haspopup="dialog" aria-label="Add to cart" className="login-request cartButton  button button--outlined">
            <b>MN Rewards Exclusive</b>
            <p>Click here to Login or Create Account</p>
          </a>
        )}

        {!hasSoonTag && appExclusive && (
          <a
            className={classes.notify + " appExclusive link bis-link"}
            target="_blank"
            onClick={() => window.open('https://app.musclenation.org/', '_blank')}
          >
            Shop in App
            <svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" className="icon icon-chevron-right" width="10" height="10" fill="currentColor" viewBox="0 0 320 512"><path d="M113.3 47.41l183.1 191.1c4.469 4.625 6.688 10.62 6.688 16.59s-2.219 11.97-6.688 16.59l-183.1 191.1c-9.152 9.594-24.34 9.906-33.9 .7187c-9.625-9.125-9.938-24.38-.7187-33.91l168-175.4L78.71 80.6c-9.219-9.5-8.906-24.78 .7187-33.91C88.99 37.5 104.2 37.82 113.3 47.41z"/></svg>
            {/* <svg height="24" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" style={{borderRadius: '4px', margin: '0 0 0 4px'}}>
              <rect width="100" height="100" rx="5" fill="#18181B"/>
              <path d="M46.6846 32.4732C44.0811 31.1343 39.4128 30.7771 35.6421 30.7771H27.9658C43.3179 31.804 39.592 37.0268 38.2456 40.2409C36.8987 43.455 25.4072 69.3467 25.4072 69.3467H34.9686C40.0409 58.7221 48.3008 39.2591 48.8844 37.5625C49.4681 35.8664 49.2885 33.8126 46.685 32.4737L46.6846 32.4732Z" fill="white"/>
              <path d="M72.0679 42.3395C69.4644 40.9915 64.7957 40.6322 61.0254 40.6322H53.3491C68.7012 41.6655 64.9753 46.9231 63.6288 50.1583C62.282 53.3934 55.3146 69.3473 55.3146 69.3473H64.876C69.9483 58.6532 73.6837 49.17 74.2673 47.4623C74.8509 45.755 74.6714 43.6879 72.0679 42.3399V42.3395Z" fill="white"/>
              <path d="M52.5354 42.0711L40.2061 69.3468H49.8421L61.3936 43.6783C56.4258 41.7736 52.5354 42.0715 52.5354 42.0715V42.0711Z" fill="white"/>
            </svg> */}
          </a>
        )}



      </div>


      <ProductLightBox
        hit={hit}
        title={title}
        subtitle={subtitle}
        badgeText={badgeText}
        canAddToCart={canAddToCart}
        cartState={cartState}
        isMedium={isMedium}
        wishState={wishState}
        // setWishState={setWishState}
        canWishlist={canWishlist}
        Dialog={Dialog}
        currency={currency}
        ProductPrice={ProductPrice}
        messageStyles={messageStyles}
        messageText={messageText}
        handleWishClick={handleWishClick}
        hasOneVariant={hasOneVariant}
      />
    </div>
  );
};

ProductCard.Skeleton = () => (
  <div>
    <div className="media media--portrait">
      <div className={classes.skeleton}>&nbsp;</div>
    </div>
    <div className="product-card__info">
      <div className="product-card__header">
        <span
          className={clsx(
            classes.skeleton,
            classes.text,
            "product-card__title"
          )}
        >
          &nbsp;
        </span>
        <span
          style={{ width: "50%" }}
          className={clsx(
            classes.skeleton,
            classes.text,
            "product-card__subtitle"
          )}
        >
          &nbsp;
        </span>
      </div>
    </div>
  </div>
);